import { useForm } from "react-hook-form";
import {
  useCardNumberValidator,
  useCountryCode,
} from "@apac/components/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { nameRegex } from "@hopper-b2b/utilities";

export type PaymentFormValues = {
  cardNumber: string;
  nameOnCard: string;
  expiryDate: string;
  cvv: string;
  zipCode: string;
  country: string;
};

const getFormattedExpiryDate = (
  value: string,
  yearDigit: "four" | "two"
): string => {
  // Remove all non-numeric characters
  const cleanedValue = value.replace(/\D/g, "");

  const expectedLength = yearDigit === "four" ? 6 : 4;
  const truncatedValue = cleanedValue.slice(0, expectedLength);

  // Format validation for month
  let formattedMonth = truncatedValue.slice(0, 2);
  if (formattedMonth.length === 2) {
    const month = parseInt(formattedMonth, 10);
    if (month > 12) {
      formattedMonth = "12";
    } else if (month === 0) {
      formattedMonth = "01";
    }
  }

  // Combine month, slash, and year
  const formattedValue =
    formattedMonth +
    (truncatedValue.length > 2
      ? `/${truncatedValue.slice(2, expectedLength)}`
      : "");

  return formattedValue;
};

export const usePaymentForm = (yearDigit: "two" | "four") => {
  const { t, brand } = useI18nContext();

  const countryCode = useCountryCode();

  const formContext = useForm<PaymentFormValues>({
    defaultValues: {
      nameOnCard: "",
      expiryDate: "",
      cvv: "",
      cardNumber: "",
      zipCode: "",
      country: countryCode,
    },
    mode: "onBlur",
  });

  formContext.register("nameOnCard", {
    required: t("apac.paymentForm.errors.nameOnCardRequired"),
    minLength: {
      value: 2,
      message: t("firstNameMinCharacterError"),
    },
    pattern: {
      value: nameRegex,
      message: t("noSpecialCharactersError"),
    },
  });

  formContext.register("expiryDate", {
    required: t("apac.paymentForm.errors.expiryDateRequired"),
    validate: (value) => {
      // Remove slash and validate
      const cleanValue = value.replace(/\//g, "");

      // Check length based on year digit configuration
      const expectedLength = yearDigit === "four" ? 6 : 4;
      if (cleanValue.length !== expectedLength) {
        return t("apac.paymentForm.errors.invalidExpiryDate");
      }

      const month = parseInt(cleanValue.slice(0, 2), 10);
      const year =
        yearDigit === "four"
          ? parseInt(cleanValue.slice(2), 10)
          : parseInt(cleanValue.slice(2), 10) + 2000;

      // Basic validation
      if (month < 1 || month > 12) {
        return t("apac.paymentForm.errors.invalidMonth");
      }

      // Get current year and month
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      // Check if the date is in the future
      if (
        year < currentYear ||
        (year === currentYear && month < currentMonth)
      ) {
        return t("apac.paymentForm.errors.cardExpired");
      }

      return true;
    },
    onChange: (e) => {
      const { value } = e.target;

      formContext.setValue(
        "expiryDate",
        getFormattedExpiryDate(value, yearDigit)
      );
    },
  });

  formContext.register("cvv", {
    required: t("apac.paymentForm.errors.cvvRequired"),
    minLength: {
      value: 3,
      message: t("apac.paymentForm.errors.cardNumberRequired"),
    },
  });

  formContext.register("cardNumber", {
    required: t("apac.paymentForm.errors.cardNumberRequired"),
    minLength: {
      value: 16,
      message: t("apac.paymentForm.errors.cardNumberRequired"),
    },
    validate: useCardNumberValidator(),
  });

  if (!brand.hidePaymentZipCode) {
    formContext.register("zipCode", {
      required: t("apac.paymentForm.errors.zipCodeRequired"),
    });
  }

  return {
    formContext,
  };
};
